import React, { useState, useEffect } from 'react';
import { getUserRole, ROLE_NAV, ROLE_LABELS, ROLE_COLORS, ROLE_BG } from '../roles';
import { useSupportStats, useAlertCount, useWaitlistStats } from '../hooks/useAnalytics';
import usePresence, { PresenceContext } from '../hooks/usePresence';
import InviteModal from './InviteModal';

import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmtCallDur(s) { const m = Math.floor(s / 60); return m + ':' + String(s % 60).padStart(2, '0'); }

const CONSOLE_SCREENS = new Set(['fraud','notifsettings','command','overview','revenuehub','bookingops','community','users','retention','directory','sana','system','visaportals','partners','marketingconsole','passmanager','languages','knowledgebase','roles','brandkit','campaigns','planner','studio','communications','dispatch','chat','appstudio','locations','content','locationeditor','verifyqueue','locationprofile','sanaeditor','featured','notices','betaaccess','aiassistant','milestones','waitlist','notifications','dataops','dataquality','duplicates','bulkeditor','tagnormalizer','geocoding','locationquality','datacompleteness']);

const NAV = [
  { section: 'Manage', items: [
    { id: 'passmanager', label: 'Members' },
    { id: 'moderation',  label: 'Drift Reports' },
    { id: 'community',   label: 'Community' },
    { id: 'content',     label: 'Content' },
    { id: 'partners',    label: 'Partners' },
    { id: 'visaportals', label: 'Visa Portals' },
    { id: 'pathways',    label: 'Pathways' },
    { id: 'pathwayroutes', label: 'Pathway routes' },
    { id: 'visarules',   label: 'Visa rules' },
    { id: 'directorygrowth', label: 'Directory Growth' },
  ]},
  { section: 'Analytics', items: [
    { id: 'command',   label: 'Command Center' },
    { id: 'revenuehub', label: 'Revenue' },
    { id: 'bookingops', label: 'Bookings' },
    { id: 'users',     label: 'Users' },
    { id: 'directory', label: 'Directory', badge: '4', bc: '#dc3545' },
    { id: 'sana',      label: 'Sana' },
    { id: 'system',    label: 'System',    badge: '2', bc: '#dc3545' },
    { id: 'fraud',     label: 'Fraud & Abuse' },
  ]},
  { section: 'Marketing', items: [
    { id: 'marketingconsole', label: 'Overview' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'brandkit',  label: 'Brand Kit' },
    { id: 'waitlist',  label: 'Waitlist', badgeFn: 'waitlist' },
    // 2 Nov 2026. The Push console was registered in App.js as `notifications`
    // and had a title in TITLES, but NO sidebar item pointed at it, so it was
    // unreachable by clicking -- you had to know the route. The owner went
    // looking for it and found My Notifications and the Alerts Centre instead.
    { id: 'notifications', label: 'Push' },
  ]},
  { section: 'Team', items: [
    { id: 'planner',        label: 'Planner' },
    { id: 'communications', label: 'Dispatch', badgeFn: true },
    { id: 'studio',         label: 'Studio' },
    { id: 'roles',        label: 'Roles & Access' },
    { id: 'notifsettings',  label: 'My Notifications' },
    { id: 'milestones', label: 'Milestones' },
  ]},
];

const TITLES = {
  command:'Command Center',
  overview:'Command Center', growth:'Growth', users:'Users',
  directory:'Directory', sana:'Sana', system:'System',
  locations:'Locations', passmanager:'Members', content:'Content',
  campaigns:'Campaigns', waitlist:'Waitlist',
  partners:'Partners', visaportals:'Visa Portals',
  planner:'Planner', communications:'Dispatch', dispatch:'Dispatch', languages:'Languages', roles:'Roles & Access',
  userdetail:'User Lookup', travelprofiles:'Travel Profiles',
  nps:'NPS & Feedback', retention:'Retention', saved:'Saved Locations',
  journey:'User Journey', cohort:'Cohort Retention', peak:'Peak Usage',
  revenue:'Revenue & LTV', revenuehub:'Revenue', subscriptions:'Subscriptions', refundqueue:'Refund Queue', bookingops:'Booking Ops', community:'Community',
  data:'Data Health', dataquality:'Data Quality', verifyqueue:'Verify Queue',
  locationeditor:'Location Editor', bulkeditor:'Bulk Editor',
  locationprofile:'Location Search', duplicates:'Data Cleanup',
  tagnormalizer:'Tag Normalizer', locationquality:'Location Quality',
  coverage:'Coverage', pipeline:'Pipeline', geocoding:'Geocoding',
  pathways:'Pathways', pathwayroutes:'Pathway routes', visarules:'Visa rules', directorygrowth:'Directory Growth',
  coverageplanner:'Coverage Planner', synctrigger:'Sync Trigger',
  email:'Email', social:'Social',
  abtest:'A/B Tests', utm:'UTM & Referral', search:'Search',
  commshub:'Comms Hub', sanaeditor:'Sana Editor',
  notices:'Notices', featured:'App Content', betaaccess:'Beta Access',
  aiassistant:'AI Assistant', milestones:'Milestones',
  fraud:'Fraud & Abuse', launchreadiness:'Launch Tracker',
  perf:'Performance', apicostmonitor:'API Costs', teamproductivity:'Team Productivity',
  activitylog:'Activity Log', team:'Team Hub',
  notifications:'Push Notifications', telegram:'Telegram',
  studio:'Studio', brandkit:'Brand Kit', appstudio:'The App', devsetup:'Developer Setup',
};

const NON_ANALYTICS = [
  'data','verifyqueue','locationeditor','locationprofile','duplicates','coverage','pipeline',
  'dataquality','bulkeditor','tagnormalizer','datacompleteness','coverageplanner','locationquality','geocoding','synctrigger',
  'commshub','campaigns','waitlist','email','social','abtest',
  'telegram','utm','growth',
  'sanaeditor','featured','passmanager','betaaccess','launchreadiness','system','pathways','pathwayroutes','visarules','directorygrowth',
  'aiassistant','notices','milestones','apicostmonitor','fraud','perf',
  'planner','communications','studio','brandkit','appstudio','devsetup','chat','languages','roles','activitylog','knowledgebase','partners','visaportals',
  'notifications','teamproductivity','notifsettings','bookingops','community',
];

const WORDMARK = (
  <svg viewBox="0 0 1080 164.33" style={{height:14,width:'auto',display:'block'}}>
    <defs><style>{'.wl{fill:#e0f5fc}'}</style></defs>
    <path className="wl" d="M63.72,133.19c15.09,0,24.19-7.43,24.19-17.25,0-34.25-83.12-9.58-83.12-70.19C4.79,19.64,28.99,0,61.32,0s57.73,22.28,57.73,48.87h-35.93c0-8.86-9.34-17.73-21.8-17.73s-21.8,6.23-21.8,14.61c0,29.94,84.32,11.5,84.32,66.35,0,29.94-26.83,52.22-62.52,52.22S0,138.94,0,105.16h34.73c0,16.05,9.1,28.03,28.99,28.03Z"/>
    <path className="wl" d="M270.32,159.54v-15.33c-6.95,8.62-27.31,19.88-49.11,19.88-40.24,0-76.89-36.65-76.89-81.69S180.97,1.2,226.01,1.2s79.05,36.41,79.05,81.21v77.13h-34.73ZM226.01,132.95c24.91,0,45.75-21.8,45.75-50.54s-20.84-50.06-45.75-50.06-45.75,21.56-45.75,50.06,20.84,50.54,45.75,50.54Z"/>
    <path className="wl" d="M411.01,31.38c-21.32,0-35.69,14.85-35.69,37.13v91.03h-34.73v-91.03C340.58,27.79,368.85.48,411.01.24c42.16.24,70.43,27.55,70.43,68.27v91.03h-34.73v-91.03c0-22.28-14.37-37.13-35.69-37.13Z"/>
    <path className="wl" d="M510.01,82.4c0-44.79,36.65-81.21,81.68-81.21s81.69,36.41,81.69,81.21-36.65,81.69-81.69,81.69-81.68-36.65-81.68-81.69ZM591.69,132.95c24.91,0,45.75-21.8,45.75-50.54s-20.84-50.06-45.75-50.06-45.75,21.56-45.75,50.06,20.84,50.54,45.75,50.54Z"/>
    <path className="wl" d="M738.87,3.83v155.7h-34.74V3.83h34.74Z"/>
    <path className="wl" d="M846.73.24v31.14c-21.32,0-35.69,14.85-35.69,37.13v91.03h-34.73v-91.03c0-40.72,28.27-68.03,70.42-68.27Z"/>
    <path className="wl" d="M940.3,164.09c-45.03,0-81.68-36.65-81.68-81.69S895.26,1.2,940.3,1.2c41.44,0,86.72,34.73,80.49,92.7h-125.04c4.55,22.76,23,39.05,44.55,39.05,17.49,0,33.06-10.78,40.48-27.55h37.37c-9.82,33.77-40.96,58.69-77.85,58.69ZM982.7,64.92c-6.23-19.64-23-32.58-42.4-32.58s-36.65,13.17-43.12,32.82l85.52-.24Z"/>
    <path className="wl" d="M1058.5,9.54c5.33,0,9.31,3.43,9.31,7.99,0,4.06-3.15,7.22-7.62,7.85l9.09,10.78h-6.12l-8.98-10.65h-2.97v10.65h-5.19V9.54h12.49ZM1058.5,21.18c2.26,0,3.94-1.56,3.94-3.66s-1.68-3.66-3.94-3.66h-7.3v7.32h7.3Z"/>
    <path className="wl" d="M1056.54,47.07c-12.94,0-23.46-10.52-23.46-23.46S1043.6.15,1056.54.15s23.46,10.52,23.46,23.46-10.52,23.46-23.46,23.46ZM1056.54,3.13c-11.29,0-20.48,9.19-20.48,20.48s9.19,20.48,20.48,20.48,20.48-9.19,20.48-20.48-9.19-20.48-20.48-20.48Z"/>
  </svg>
);

// ── Responsive breakpoint hook ────────────────────────────────────────────────
function useBreakpoint() {
  const [w, setW] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ── Mobile nav icons ──────────────────────────────────────────────────────────
function IconOverview() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}
function IconHelp() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
    </svg>
  );
}
function IconData() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3"/>
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
    </svg>
  );
}
function IconLocations() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5"/>
    </svg>
  );
}
function IconMore() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <circle cx="5" cy="12" r="1.2" fill="currentColor"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/>
      <circle cx="19" cy="12" r="1.2" fill="currentColor"/>
    </svg>
  );
}

// ── Mobile drawer (slide-in full nav) ─────────────────────────────────────────
function MobileDrawer({ open, onClose, onNav, screen, supportStats, alertCount, waitlistWaiting }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      {/* Scrim */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }}
      />
      {/* Drawer panel */}
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0,
        width: 'min(280px, 75vw)', background: '#0f1923',
        display: 'flex', flexDirection: 'column',
        overflowY: 'auto', fontFamily: F,
      }}>
        {/* Drawer header */}
        <div style={{
          padding: '16px 16px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          {WORDMARK}
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'rgba(175,225,246,0.5)', padding: 4,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        {/* Nav items */}
        <div style={{ flex: 1, padding: '8px 0 24px' }}>
          {NAV.map(({ section, items }) => (
            <div key={section} style={{ marginTop: 4 }}>
              <div style={{
                borderTop: '1px solid rgba(175,225,246,0.07)',
                margin: '4px 0 8px',
              }} />
              <div style={{
                fontSize: '.62rem', color: 'rgba(175,225,246,0.45)',
                letterSpacing: '.1em', textTransform: 'uppercase',
                padding: '0 16px', marginBottom: 2, fontWeight: 700,
              }}>
                {section}
              </div>
              {items.map(({ id, label, badge, bc, badgeFn }) => {
                const active = screen === id;
                return (
                  <button
                    key={id}
                    onClick={() => { onNav(id); onClose(); }}
                    style={{
                      display: 'flex', alignItems: 'center',
                      width: '100%', padding: '0 16px', height: 40,
                      fontSize: '.85rem', cursor: 'pointer', border: 'none',
                      background: active ? 'rgba(175,225,246,0.1)' : 'transparent',
                      color: active ? '#e8f4fb' : 'rgba(175,225,246,0.55)',
                      fontFamily: F, fontWeight: active ? 500 : 400,
                      textAlign: 'left',
                      borderLeft: active ? '2px solid #6FBFE0' : '2px solid transparent',
                    }}
                  >
                    <span style={{ flex: 1 }}>{label}</span>
                    {(badge ||
                      (badgeFn === true    && supportStats?.open > 0) ||
                      (badgeFn === 'alerts' && alertCount > 0) ||
                      (badgeFn === 'waitlist' && (waitlistWaiting||0) > 0)
                    ) && (
                      <span style={{
                        background: badgeFn === 'alerts' ? '#e05c5c' : badgeFn === 'waitlist' ? '#2da06a' : badgeFn ? '#e53935' : bc,
                        color: '#fff', fontSize: '.58rem', borderRadius: 4,
                        padding: '1px 5px', fontWeight: 700, lineHeight: 1.5,
                      }}>
                        {badgeFn === 'waitlist' ? waitlistWaiting :
                         badgeFn === true ? (supportStats.unassigned > 0 ? supportStats.unassigned : supportStats.open) :
                         badgeFn === 'alerts' ? alertCount :
                         badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Sidebar nav items (shared between desktop + tablet) ───────────────────────
const COLLAPSE_KEY = 'sanoire_nav_collapsed';
function getCollapsed() {
  try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || '[]'); } catch (_e) { return []; }
}
function setCollapsed(arr) {
  try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(arr)); } catch (_e) {}
}

function SidebarNav({ screen, onNav, supportStats, alertCount, sidebarWidth, userEmail, waitlistWaiting }) {
  const [collapsed, setCollapsedState] = React.useState(getCollapsed);
  const role = getUserRole(userEmail);
  const allowedIds = ROLE_NAV[role];
  const labelStyle = {
    fontSize: sidebarWidth < 180 ? '.6rem' : '.65rem',
    color: 'rgba(255,255,255,0.3)',
    letterSpacing: '.1em', textTransform: 'uppercase',
    padding: sidebarWidth < 180 ? '0 10px' : '0 14px',
    marginBottom: 2, fontWeight: 600,
  };
  const toggleSection = (section) => {
    const next = collapsed.includes(section)
      ? collapsed.filter(s => s !== section)
      : [...collapsed, section];
    setCollapsedState(next);
    setCollapsed(next);
  };
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0 16px' }}>
      {NAV.map(({ section, items }) => {
        const visibleItems = allowedIds
          ? items.filter(({ id }) => allowedIds.has(id))
          : items;
        if (visibleItems.length === 0) return null;
        const isCollapsed = collapsed.includes(section);
        const shownItems = visibleItems;
        return (
          <div key={section} style={{ marginTop: 4 }}>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '4px 0 8px' }} />
            <button
              onClick={() => toggleSection(section)}
              style={{
                ...labelStyle, display: 'flex', alignItems: 'center', gap: 4,
                width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                paddingBottom: 4,
              }}
            >
              <span style={{ flex: 1, textAlign: 'left' }}>{section}</span>
              <span style={{ fontSize: '.6rem', color: 'rgba(255,255,255,0.25)', marginRight: sidebarWidth < 180 ? 8 : 12, transition: 'transform .15s', display: 'inline-block', transform: isCollapsed ? 'rotate(-90deg)' : 'none' }}>&#9662;</span>
            </button>
            {!isCollapsed && shownItems.map(({ id, label, badge, bc, badgeFn }) => {
              const active = screen === id;
              const _px = sidebarWidth < 180 ? '0 10px' : '0 16px';
              return (
                <button
                  key={id}
                  onClick={() => onNav(id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 0,
                    width: '100%', padding: sidebarWidth < 180 ? '0 6px' : '0 8px', height: 32,
                    fontSize: sidebarWidth < 180 ? '.74rem' : '.78rem',
                    cursor: 'pointer', border: 'none',
                    background: active ? 'rgba(185,156,109,0.12)' : 'transparent',
                    color: active ? '#ffffff' : 'rgba(255,255,255,0.52)',
                    fontFamily: F, fontWeight: active ? 500 : 400,
                    textAlign: 'left', letterSpacing: '-.01em',
                    borderLeft: 'none',
                    borderRadius: 7,
                    boxShadow: active ? 'inset 0 0 0 1px rgba(185,156,109,0.3)' : 'none',
                    transition: 'all .1s',
                  }}
                  onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}}
                  onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.52)'; }}}
                >
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: active ? 0 : 8 }}>{label}</span>
                   {active && <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#B99C6D', flexShrink: 0, marginRight: 4 }} />}
                  {(badge ||
                    (badgeFn === true    && supportStats?.open > 0) ||
                    (badgeFn === 'alerts' && alertCount > 0) ||
                    (badgeFn === 'waitlist' && (waitlistWaiting||0) > 0)
                  ) && (
                    <span style={{
                      background: badgeFn === 'alerts' ? '#e05c5c' : badgeFn === 'waitlist' ? '#2da06a' : badgeFn ? '#e53935' : bc,
                      color: '#fff', fontSize: '.56rem',
                      borderRadius: 4, padding: '1px 5px', fontWeight: 700,
                      letterSpacing: 0, lineHeight: 1.5, flexShrink: 0,
                    }}>
                      {badgeFn === 'waitlist' ? waitlistWaiting :
                       badgeFn === true ? supportStats.open :
                       badgeFn === 'alerts' ? alertCount :
                       badge}
                    </span>
                  )}
                </button>
              );
            })}

          </div>
        );
      })}
    </div>
  );
}

export default function Layout({ screen, onNav, children, session, period, onPeriod, compare, onCompare }) {
  const bp             = useBreakpoint();
  const supportStats   = useSupportStats();
  const alertCount     = useAlertCount();
  const { waiting: waitlistWaiting } = useWaitlistStats();
  const openBadge      = supportStats?.open > 0 ? supportStats.open : null;
  const [drawerOpen,    setDrawerOpen]    = React.useState(false);
  const [headerCall,    setHeaderCall]    = React.useState(null);   // { name, type } | null
  const [headerCallDur, setHeaderCallDur] = React.useState(0);
  const [incomingCall,  setIncomingCall]  = React.useState(null); // { callerName, callerInitials, roomUrl, roomName }
  const [callDeclined,  setCallDeclined]  = React.useState(false);
  const incomingAudioRef = React.useRef(null);
  const [inviteOpen,    setInviteOpen]    = React.useState(false);
  const [userMenuOpen,  setUserMenuOpen]  = React.useState(false);
  const [livePulse, setLivePulse] = React.useState(false);
  React.useEffect(() => {
    const flash = () => { setLivePulse(true); setTimeout(() => setLivePulse(false), 1100); };
    const ch = supabase.channel('masthead-live')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'sana_invocations' }, flash)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'search_invocations' }, flash)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'user_passes' }, flash)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);
  const _mh = new Date().getHours();
  const tod = _mh < 12 ? 'morning' : _mh < 18 ? 'afternoon' : 'evening';
  const email    = session?.user?.email || '';
  const initials = email.split('@')[0].split(/[._-]/).map(s => s[0]?.toUpperCase()).join('').slice(0, 2) || 'S';
  const userRole  = getUserRole(email);
  const roleColor = ROLE_COLORS[userRole] || '#314F5C';
  const roleBg    = ROLE_BG[userRole]    || '#314F5C18';
  const joinedAt  = session?.user?.created_at
    ? new Date(session.user.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '\u2014';
  const lastSeen  = 'just now';
  const me = React.useMemo(() => ({
    initials, name: email.split('@')[0].split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' '),
    email,
  }), [email, initials]);
  const { onlineUsers } = usePresence(me, screen);

  // Listen for call state from FloatingCallOverlay
  React.useEffect(() => {
    const name = TITLES[screen] || screen;
    document.title = name ? name + ' · Sanoire Admin' : 'Sanoire Admin';
  }, [screen]);

  useEffect(() => {
    function onCallState(e) {
      // CALL-FIX-04 — capture roomUrl so the header pill click can hand it
      // off to Dispatch via sanoire:loadteamroom.
      if (e.detail?.active) { setHeaderCall({ name: e.detail.name, type: e.detail.type, roomUrl: e.detail.roomUrl || null }); setHeaderCallDur(0); }
      else { setHeaderCall(null); setHeaderCallDur(0); }
    }
    function onCallDur(e) { setHeaderCallDur(e.detail?.duration || 0); }
    window.addEventListener('sanoire:callstate', onCallState);
    window.addEventListener('sanoire:callduration', onCallDur);
    return () => {
      window.removeEventListener('sanoire:callstate', onCallState);
      window.removeEventListener('sanoire:callduration', onCallDur);
    };
  }, []);

  // Listen for incoming call broadcasts
  React.useEffect(() => {
    if (!me?.initials) return;
    import('../supabaseClient').then(({ supabase: sb }) => { // eslint-disable-line
      const ch = sb.channel('admin-calls')
        .on('broadcast', { event: 'incoming_call' }, ({ payload }) => {
          if (!payload || payload.callerInitials === me.initials) return;
          if (!payload.recipients || payload.recipients.includes(me.email) || payload.recipients.includes('all')) {
            setIncomingCall(payload);
            setCallDeclined(false);
            // Play ring tone — classic double-ring pattern
            try {
              const ctx = new (window.AudioContext || window.webkitAudioContext)();
              function playTone(freq, start, duration, vol) {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g); g.connect(ctx.destination);
                o.frequency.value = freq; o.type = 'sine';
                g.gain.setValueAtTime(0, ctx.currentTime + start);
                g.gain.linearRampToValueAtTime(vol, ctx.currentTime + start + 0.02);
                g.gain.setValueAtTime(vol, ctx.currentTime + start + duration - 0.04);
                g.gain.linearRampToValueAtTime(0, ctx.currentTime + start + duration);
                o.start(ctx.currentTime + start);
                o.stop(ctx.currentTime + start + duration);
              }
              function doubleRing() {
                // Two quick tones (480Hz + 440Hz) — classic phone ring
                playTone(480, 0,    0.4, 0.4);
                playTone(440, 0,    0.4, 0.3);
                playTone(480, 0.5,  0.4, 0.4);
                playTone(440, 0.5,  0.4, 0.3);
              }
              doubleRing();
              incomingAudioRef.current = setInterval(doubleRing, 2200);
            } catch (_e) {}
            // Auto-dismiss after 30s
            setTimeout(() => { setIncomingCall(null); clearInterval(incomingAudioRef.current); }, 30000);
          }
        })
        .subscribe();
      return () => sb.removeChannel(ch);
    });
  }, [me]); // eslint-disable-line

  const handleLogout = async () => { try { const { supabase: sb } = await import('../supabaseClient'); await sb.auth.signOut(); } catch (_e) {} window.location.reload(); };
  const BOT_TAB_SCREENS = [
    'communications','chat','languages','roles',
    'locations','passmanager','content',
    'campaigns','waitlist',
  ];

  // ── MOBILE layout ─────────────────────────────────────────────────────────────
  if (bp === 'mobile') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#f2f1ef', position: 'relative' }}>
          {/* Main content */}
          <div style={{ padding: (screen === 'communications' || screen === 'dispatch' || screen === 'chat') ? 0 : (CONSOLE_SCREENS.has(screen) ? '0 0 80px' : '16px 14px 80px') }}>
            <PresenceContext.Provider value={onlineUsers}>{children}</PresenceContext.Provider>
          </div>

        {/* Bottom nav */}
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: '#0d1d2b', height: 58,
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'stretch',
          zIndex: 500, fontFamily: F,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
          {[
            { id: 'overview',     label: 'Command',    Icon: IconOverview,   badge: null },
            { id: 'communications', label: 'Dispatch', Icon: IconHelp,       badge: openBadge },
            { id: 'locations',    label: 'Locations',  Icon: IconLocations,  badge: null },
          ].map(({ id, label, Icon, badge }) => {
            const active = screen === id || (id === 'overview' && !BOT_TAB_SCREENS.includes(screen));
            return (
              <button
                key={id}
                onClick={() => onNav(id)}
                style={{
                  flex: 1, background: 'none', border: 'none', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', gap: 3,
                  color: active ? '#AFE1F6' : 'rgba(175,225,246,0.35)',
                  borderTop: active ? '2px solid #6FBFE0' : '2px solid transparent',
                  position: 'relative',
                }}
              >
                <Icon />
                <span style={{ fontSize: '.6rem', fontWeight: active ? 600 : 400 }}>{label}</span>
                {badge != null && (
                  <span style={{
                    position: 'absolute', top: 6, right: '20%',
                    background: '#e53935', color: '#fff',
                    fontSize: '.52rem', fontWeight: 700, borderRadius: 10,
                    padding: '1px 4px', lineHeight: 1.4,
                    border: '1.5px solid #0d1d2b',
                  }}>{badge}</span>
                )}
              </button>
            );
          })}
          {/* More tab */}
          <button
            onClick={() => setDrawerOpen(true)}
            style={{
              flex: 1, background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 3,
              color: 'rgba(175,225,246,0.35)',
              borderTop: '2px solid transparent',
            }}
          >
            <IconMore />
            <span style={{ fontSize: '.6rem' }}>More</span>
          </button>
        </div>

        {/* Full nav drawer */}
        <MobileDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onNav={onNav}
          screen={screen}
          supportStats={supportStats}
          alertCount={alertCount}
            waitlistWaiting={waitlistWaiting}
        />
      </div>
    );
  }

  // ── TABLET + DESKTOP layout ───────────────────────────────────────────────────
  const sidebarWidth = bp === 'tablet' ? 150 : 220;

  return (
    <>
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: F, background: '#f2f1ef' }}>

      {/* ── SIDEBAR ─────────────────────────────────────────────────────── */}
      <div style={{
        width: sidebarWidth, flexShrink: 0, background: '#091420',
        display: 'flex', flexDirection: 'column', height: '100vh',
        overflowY: 'auto', borderRight: '1px solid rgba(185,156,109,0.2)',
      }}>
        {/* Logo */}
        <div style={{
          padding: bp === 'tablet' ? '14px 10px 12px' : '18px 16px 14px',
          borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0,
          background: 'linear-gradient(180deg, rgba(49,79,92,0.25) 0%, transparent 100%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: bp === 'tablet' ? 0 : 14 }}>
            <img src={'data:image/svg+xml;base64,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'} style={{ width: 18, height: 18, opacity: 0.85, flexShrink: 0 }} alt='' />
            {bp !== 'tablet' && WORDMARK}
            {bp !== 'tablet' && <span style={{ marginLeft: 'auto', fontSize: '.58rem', fontWeight: 600, background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', padding: '2px 7px', borderRadius: 99, letterSpacing: '.06em', flexShrink: 0 }}>ADMIN</span>}
            {Object.keys(onlineUsers).length > 0 && bp !== 'tablet' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }} title={Object.values(onlineUsers).map(u => u.name).join(', ') + ' online'}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#2da06a', boxShadow: '0 0 4px rgba(45,160,106,0.6)' }} />
                <span style={{ fontSize: '.58rem', color: 'rgba(255,255,255,0.45)' }}>{Object.keys(onlineUsers).length}</span>
              </div>
            )}
          </div>

        </div>

        {/* Nav */}
        <SidebarNav
          screen={screen}
          onNav={onNav}
          supportStats={supportStats}
          alertCount={alertCount}
              waitlistWaiting={waitlistWaiting}
          sidebarWidth={sidebarWidth}
          userEmail={email}
        />

        {/* User footer */}
        <div style={{
          padding: bp === 'tablet' ? '8px 8px 10px' : '10px 12px 12px',
          borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0, position: 'relative',
        }}>
          <button
            onClick={() => setUserMenuOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', background: 'none', border: 'none',
              cursor: 'pointer', padding: '6px 4px', borderRadius: 7,
              fontFamily: F,
            }}
          >
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: 'linear-gradient(135deg, #314F5C 0%, #4a7a8c 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '.62rem', fontWeight: 700, color: '#E0F5FC', flexShrink: 0,
              border: '1px solid rgba(175,225,246,0.2)',
            }}>
              {initials}
            </div>
            {bp !== 'tablet' && (
              <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '.72rem', color: '#ffffff', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1 }}>{email.split('@')[0].split('.').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</div>
                  <div style={{ fontSize: '.62rem', color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>Admin</div>
                </div>
              </div>
            )}
            {bp !== 'tablet' && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                <path d="M3 4.5L6 7.5L9 4.5" stroke="rgba(175,225,246,0.3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
          {userMenuOpen && (
            <div style={{
              position: 'absolute', bottom: 62, left: 10, right: 10,
              background: '#131e28', border: '1px solid rgba(175,225,246,0.13)',
              borderRadius: 12, overflow: 'hidden',
              boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            }}>
              {/* Avatar + email + role pill */}
              <div style={{ padding: '14px 14px 11px', borderBottom: '1px solid rgba(175,225,246,0.08)', background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: roleColor + '33',
                  border: '1.5px solid ' + roleColor + '66',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '.68rem', fontWeight: 700, color: roleColor, marginBottom: 9 }}>{initials}</div>
                <div style={{ fontSize: '.72rem', color: 'rgba(175,225,246,0.85)', fontWeight: 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 5 }}>{email}</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 20,
                  background: roleBg, border: '1px solid ' + roleColor + '44',
                  fontSize: '.62rem', fontWeight: 600, color: roleColor }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: roleColor, display: 'inline-block' }} />
                  {ROLE_LABELS[userRole]}
                </div>
              </div>
              {/* Stats: joined, last seen, sessions, role */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '10px 14px',
                gap: '8px 14px', borderBottom: '1px solid rgba(175,225,246,0.07)' }}>
                {[{ l: 'Joined', v: joinedAt }, { l: 'Last seen', v: lastSeen },
                  { l: 'Sessions', v: '—' }, { l: 'Role', v: ROLE_LABELS[userRole] }]
                  .map(({ l, v }) => (
                  <div key={l}>
                    <div style={{ fontSize: '.55rem', color: 'rgba(175,225,246,0.3)', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 2 }}>{l}</div>
                    <div style={{ fontSize: '.68rem', color: 'rgba(175,225,246,0.65)', fontWeight: 500 }}>{v}</div>
                  </div>
                ))}
              </div>
              {/* Action buttons */}
              <div style={{ padding: '6px 8px' }}>
                {(userRole === 'c_suite' || userRole === 'admin') && (
                  <button onClick={() => { setUserMenuOpen(false); setInviteOpen(true); }}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, background: 'none',
                      border: 'none', cursor: 'pointer', fontFamily: F, fontSize: '.72rem',
                      color: 'rgba(175,225,246,0.55)', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="5" cy="4" r="2.5"/><path d="M1 11c0-2.2 1.8-4 4-4s4 1.8 4 4"/><path d="M9.5 1.5v3M8 3h3"/></svg>
                    Invite a team member
                  </button>
                )}
                <button onClick={() => { setUserMenuOpen(false); onNav('roles'); }}
                  style={{ width: '100%', padding: '6px 8px', borderRadius: 6, background: 'none',
                    border: 'none', cursor: 'pointer', fontFamily: F, fontSize: '.72rem',
                    color: 'rgba(175,225,246,0.55)', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="3.5" width="10" height="7.5" rx="1"/><path d="M4 3.5V3a2 2 0 014 0v.5"/></svg>
                  Roles & access
                </button>
                <div style={{ height: 1, background: 'rgba(175,225,246,0.07)', margin: '3px 0' }} />
                <button onClick={handleLogout}
                  style={{ width: '100%', padding: '6px 8px', borderRadius: 6, background: 'none',
                    border: 'none', cursor: 'pointer', fontFamily: F, fontSize: '.72rem',
                    color: 'rgba(220,100,100,0.7)', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 7 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M8 8.5L10.5 6 8 3.5M10.5 6H4.5M4.5 1.5H3a1 1 0 00-1 1v7a1 1 0 001 1h1.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── MAIN ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Topbar */}
        <div style={{
          background: '#0d1d2b',
          borderBottom: '1px solid rgba(185,156,109,0.35)',
          padding: bp === 'tablet' ? '0 16px' : '0 24px',
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          height: 72, flexShrink: 0,
        }}>
          {/* Left: title + period */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '.66rem', fontWeight: 600, letterSpacing: '.22em', color: '#B99C6D', textTransform: 'uppercase', whiteSpace: 'nowrap', marginBottom: 2, fontFamily: F }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#5dcaa5', opacity: livePulse ? 1 : 0.5, boxShadow: livePulse ? '0 0 8px #5dcaa5' : 'none', transition: 'opacity .25s, box-shadow .25s', flexShrink: 0 }} />
                {TITLES[screen] || screen} · {livePulse ? 'live update' : 'live'}
              </div>
              <h1 style={{ fontFamily: 'Georgia,"Times New Roman",serif', fontStyle: 'italic', fontWeight: 400, fontSize: '1.18rem', color: '#F2EEE7', margin: 0, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                This {tod} at Sanoire<span style={{ color: '#B99C6D' }}>.</span>
              </h1>
            </div>
            {!NON_ANALYTICS.includes(screen) && (
              <div style={{ display: 'flex', background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: 2, gap: 1 }}>
                {['7d','30d','90d','All'].map(p => (
                  <button key={p} onClick={() => onPeriod(p)} style={{
                    padding: bp === 'tablet' ? '3px 8px' : '4px 11px',
                    borderRadius: 6, fontSize: '.75rem',
                    cursor: 'pointer', border: 'none', fontFamily: F,
                    background: period === p ? '#F2EEE7' : 'transparent',
                    color: period === p ? '#15242e' : 'rgba(242,238,231,0.55)',
                    fontWeight: period === p ? 600 : 400,
                    boxShadow: period === p ? '0 1px 3px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)' : 'none',
                    transition: 'all .1s',
                  }}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: compare + export + avatar */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {!NON_ANALYTICS.includes(screen) && bp !== 'tablet' && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: 5,
                fontSize: '.75rem', color: 'rgba(242,238,231,0.6)', cursor: 'pointer',
                userSelect: 'none',
              }}>
                <input type="checkbox" checked={compare} onChange={e => onCompare(e.target.checked)}
                  style={{ accentColor: '#B99C6D', width: 13, height: 13 }} />
                Compare
              </label>
            )}
            {bp !== 'tablet' && (
              <button onClick={() => alert('CSV export ready')} style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: 'transparent', color: '#B99C6D',
                border: '1px solid rgba(185,156,109,0.35)', borderRadius: 7,
                padding: '5px 11px', fontSize: '.75rem',
                cursor: 'pointer', fontFamily: F, fontWeight: 500,
              }}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M6 1v7M3 5l3 3 3-3M1 9v.5A1.5 1.5 0 002.5 11h7a1.5 1.5 0 001.5-1.5V9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Export
              </button>
            )}
            <button
              onClick={() => { const e = new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }); window.dispatchEvent(e); }}
              title="Search (Cmd+K)"
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.06)', color: 'rgba(242,238,231,0.55)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, padding: '5px 11px', fontSize: '.72rem', cursor: 'pointer', fontFamily: F }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <kbd style={{ fontSize: '.62rem', color: 'rgba(242,238,231,0.4)', background: 'transparent', border: 'none', padding: 0 }}>⌘K</kbd>
            </button>
            {/* Inline call pill — shown when on non-Dispatch screens */}
            {headerCall && screen !== 'communications' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                background: '#0f1923', borderRadius: 20, padding: '4px 10px 4px 8px',
                border: '1px solid rgba(175,225,246,0.15)', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}
                onClick={() => {
                  // CALL-FIX-04 — navigate to communications AND hand off the
                  // active roomUrl so Dispatch loads the iframe. Without the
                  // loadteamroom event, clicking the pill landed the user on
                  // an empty calls screen.
                  window.dispatchEvent(new CustomEvent('sanoire:navigate', { detail: { screen: 'communications' } }));
                  if (headerCall.roomUrl) {
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('sanoire:loadteamroom', {
                        detail: { roomUrl: headerCall.roomUrl, name: headerCall.name }
                      }));
                    }, 80);
                  }
                }}
                title={'On a call with ' + headerCall.name + ' — click to go to Dispatch'}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2da06a',
                  display: 'inline-block', flexShrink: 0,
                  animation: 'callRing 1.5s ease-in-out infinite' }} />
                <span style={{ fontSize: '.66rem', fontWeight: 600, color: '#e8f4fb',
                  whiteSpace: 'nowrap', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {headerCall.name}
                </span>
                <span style={{ fontSize: '.58rem', color: 'rgba(175,225,246,0.5)',
                  fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                  {fmtCallDur(headerCallDur)}
                </span>
                <button
                  onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('sanoire:endcall')); }}
                  title='End call'
                  style={{ width: 18, height: 18, borderRadius: '50%', background: '#c62828',
                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexShrink: 0, padding: 0 }}>
                  <svg width='8' height='8' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='3' strokeLinecap='round'>
                    <line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/>
                  </svg>
                </button>
              </div>
            )}
            <div
              onClick={() => setUserMenuOpen(o => !o)}
              style={{
                width: 30, height: 30, borderRadius: 8,
                background: 'linear-gradient(135deg, #314F5C 0%, #4a7a8c 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '.65rem', fontWeight: 700, color: '#E0F5FC', cursor: 'pointer',
                border: '1px solid rgba(49,79,92,0.1)',
              }}
            >
              {initials}
            </div>
          </div>
        </div>

        {/* Compare banner */}
        {compare && !NON_ANALYTICS.includes(screen) && (
          <div style={{
            background: '#edf4f7', borderBottom: '1px solid #cee0ea',
            padding: '7px 24px', fontSize: '.75rem', color: '#314F5C',
            display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#314F5C' }} />
            Comparing current {period} vs previous — dashed line shows prior period
          </div>
        )}

        {/* Scrollable content */}
        <div style={{
          flex: 1, overflowY: 'auto',
          padding: CONSOLE_SCREENS.has(screen) ? 0 : (bp === 'tablet' ? '16px' : '22px 24px'),
          background: '#f2f1ef',
        }}>
          <PresenceContext.Provider value={onlineUsers}>{children}</PresenceContext.Provider>
        </div>
      </div>
    </div>
    {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} onSuccess={() => {}} />}

    {/* Incoming call banner — bottom-right corner toast */}
    {incomingCall && !callDeclined && (
      <div style={{ position: 'fixed', bottom: 'calc(24px + env(safe-area-inset-bottom))', right: 12, left: bp === 'mobile' ? 12 : 'auto', zIndex: 9999,
        width: bp === 'mobile' ? 'auto' : 320, maxWidth: 'calc(100vw - 24px)', background: '#0f1923', borderRadius: 18,
        border: '1px solid rgba(175,225,246,0.18)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.45)', fontFamily: F,
        animation: 'callSlideIn .25s cubic-bezier(.22,1,.36,1)' }}>
        <div style={{ padding: '16px 16px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Avatar with pulse rings */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            {[0,1].map(i => (
              <div key={i} style={{ position: 'absolute',
                top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                width: 44, height: 44, borderRadius: '50%',
                border: '1.5px solid rgba(45,160,106,0.5)',
                animation: `callRing 1.5s ${i*0.6}s ease-out infinite` }} />
            ))}
            <div style={{ width: 42, height: 42, borderRadius: 13, background: '#314F5C',
              color: '#AFE1F6', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '.78rem', fontWeight: 700, position: 'relative', zIndex: 1 }}>
              {incomingCall.callerInitials}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '.82rem', fontWeight: 700, color: '#e8f4fb', marginBottom: 2 }}>{incomingCall.callerName}</div>
            <div style={{ fontSize: '.66rem', color: '#2da06a', display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2da06a',
                display: 'inline-block', animation: 'callRing 1.2s ease-in-out infinite', flexShrink: 0 }} />
              Incoming call…
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '0 16px 16px' }}>
          <button onClick={() => { clearInterval(incomingAudioRef.current); setCallDeclined(true); setIncomingCall(null); }}
            style={{ flex: 1, height: 38, borderRadius: 10, background: 'rgba(198,40,40,0.15)',
              border: '1px solid rgba(198,40,40,0.3)', cursor: 'pointer', color: '#ef5350',
              fontSize: '.74rem', fontWeight: 600, fontFamily: F,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'>
              <path d='M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45c1.12.43 2.33.65 3.53.65a2 2 0 012 2v3.5a2 2 0 01-2 2A18.5 18.5 0 011.5 3a2 2 0 012-2H7a2 2 0 012 2c0 1.2.22 2.41.65 3.53a2 2 0 01-.45 2.11L7.93 9.9'/><line x1='23' y1='1' x2='1' y2='23'/>
            </svg>
            Decline
          </button>
          <button onClick={() => { clearInterval(incomingAudioRef.current);
              window.dispatchEvent(new CustomEvent('sanoire:navigate', { detail: { screen: 'communications' } }));
              window.dispatchEvent(new CustomEvent('sanoire:startcall', { detail: { type: 'dm', id: incomingCall.callerInitials, name: incomingCall.callerName, roomUrl: incomingCall.roomUrl } }));
              setIncomingCall(null);
            }}
            style={{ flex: 1, height: 38, borderRadius: 10, background: '#2da06a',
              border: 'none', cursor: 'pointer', color: '#fff',
              fontSize: '.74rem', fontWeight: 600, fontFamily: F,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              boxShadow: '0 4px 14px rgba(45,160,106,0.35)' }}>
            <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round'>
              <path d='M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.28a16 16 0 006.29 6.29l1.46-1.46a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 15.31z'/>
            </svg>
            Accept
          </button>
        </div>
      </div>
    )}

    {/* Keyframe for call ring animation */}
    <style>{`@keyframes callRing { 0% { transform: scale(1); opacity: 0.8; } 100% { transform: scale(2); opacity: 0; } } @keyframes callSlideIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </>
  );
}

export { F as FONT, NON_ANALYTICS };
